import React, { useEffect, useState } from "react";
import {
  useGetApplicationQuery,
  usePostApplicantAddressMutation,
  usePostUpsertApplicationMutation,
} from "@/store/service";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ApplicationStep } from "@/enum/ApplicationStep";
import Applicants from "@/model/interface/form/Applicants";
import ApplicantAddress from "./ApplicantAddress";
import { daysDifferenceBetweenDates } from "@/util/date";
import { AddressStatus } from "@/enum/AddressStatus";
import { useApplicantChecks } from "@/components/provider/ApplicantChecksProvider";
import { isDebugForms, getDebugNextStepUrl } from "@/util/debug";
import { applicantsAddressSchema } from "@/model/schemas";
import { zodFormResolver } from "@/util/form";
import Skeleton from "react-loading-skeleton";

const ApplicantAddressWrapper = () => {
  const navigate = useNavigate();
  const { applicant, applicationId, applicantType } = useApplicantChecks();

  const form = useForm<Applicants>({
    resolver: zodFormResolver<Applicants>(applicantsAddressSchema),
    mode: "onTouched",
    defaultValues: {},
  });
  const { control, watch, register, formState, handleSubmit, getValues } = form;

  const applicantFormType = applicantType === "co-applicant" ? "coApplicant" : "mainApplicant";
  const currentAddressDate = watch(`mainApplicant.address.addressHeldSince`);
  const currentDate = new Date();
  const threeYearsInDays = 3 * 365;
  const [showPreviousAddress, setShowPreviousAddress] = useState(
    currentAddressDate && daysDifferenceBetweenDates(new Date(currentAddressDate), currentDate) < threeYearsInDays,
  );

  const { data: application } = useGetApplicationQuery(applicationId);
  const [postApplication, postApplicationResult] = usePostUpsertApplicationMutation();
  const [postApplicantAddress, postApplicantAddressResult] = usePostApplicantAddressMutation();
  const [updateApplication, setUpdateApplication] = useState(false);

  useEffect(() => {
    if (applicant?.previousAddress) {
      setShowPreviousAddress(true);
    }
  }, [applicant?.previousAddress]);

  useEffect(() => {
    if (updateApplication) {
      const step =
        applicantType === "main-applicant" ? ApplicationStep.ApplicantAddress : ApplicationStep.CoApplicantAddress;
      postApplication({ id: applicationId, step });
    }
  }, [updateApplication]);

  if (!application) {
    return <Skeleton count={5} height="2.75em" className="mt-4" />;
  }
  // @ts-ignore
  const onSubmit = async (data: Applicants) => {
    const currentAddress = data[applicantFormType]?.address;
    const previousAddress = data[applicantFormType]?.previousAddress;
    if (currentAddress) {
      const data = await postApplicantAddress({
        data: { ...currentAddress, addressStatus: AddressStatus.Active },
        applicationId,
        applicantId: applicant!.id,
      }).unwrap();

      if (data && !previousAddress) {
        setUpdateApplication(true);
      }
    }
    if (previousAddress) {
      const data = await postApplicantAddress({
        data: { ...previousAddress, addressStatus: AddressStatus.Inactive },
        applicationId,
        applicantId: applicant!.id,
      }).unwrap();

      if (data) {
        setUpdateApplication(true);
      }
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDebugForms()) {
      navigate(getDebugNextStepUrl(applicationId, ApplicationStep.ApplicantAddress, applicantType));
    } else {
      handleSubmit(onSubmit)(e);
    }
  };

  return (
    <form onSubmit={handleFormSubmit} className="relative">
      <div className="w-full max-w-2xl mx-auto space-y-5">
        <h3 className="text-2xl font-semibold text-gray-900 text-center">Applicant address</h3>
        <ApplicantAddress form={form} applicant={applicant} applicantType={applicantType} addressStatus={"Active"} />
        {showPreviousAddress && (
          <>
            <div className="border-t border-gray-200" />
            <ApplicantAddress
              form={form}
              applicant={applicant}
              applicantType={applicantType}
              addressStatus={"Inactive"}
            />
          </>
        )}
        <div className="flex justify-center pt-2">
          <button
            type="submit"
            name="submit"
            className="inline-flex items-center rounded-full bg-[#9BFBE0] px-8 py-3 text-sm font-medium text-[#010305] shadow-sm hover:shadow-md transition-all"
          >
            Continue
          </button>
        </div>
      </div>
    </form>
  );
};

export default ApplicantAddressWrapper;
